import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Text, Button, Card, Spinner } from 'frosted-ui';
import { Plus16 } from '@frosted-ui/icons';
import { api } from '../services/api';
import type { Experiment, TestOverview } from '../types';
import { useCompanyId } from '../contexts/CompanyContext';
import { useIsMobile } from '../hooks/useIsMobile';
import TestCard from '../components/home/TestCard';
import UpgradeDialog from '../components/billing/UpgradeDialog';
import { needsProForNewTest, planLine, WHOP_MEMBERSHIPS_URL } from '../components/billing/plan';
import { usePlan } from '../hooks/usePlan';
import { Rise } from '../components/animation';
import LoadFailed from '../components/LoadFailed';

export type HomeFilter = 'live' | 'drafts' | 'ended';

const FILTERS: Array<{ id: HomeFilter; label: string }> = [
  { id: 'live', label: 'Live' },
  { id: 'drafts', label: 'Drafts & paused' },
  { id: 'ended', label: 'Ended' },
];

/** Live = running only; drafts and paused tests have their own filter. */
export function homeFilterOf(exp: Pick<Experiment, 'archived' | 'status'>): HomeFilter {
  if (exp.archived || exp.status === 'winner_declared') return 'ended';
  if (exp.status === 'running') return 'live';
  return 'drafts';
}

/** Open on Live, unless nothing is live and something is waiting in drafts. */
export function initialHomeFilter(experiments: Array<Pick<Experiment, 'archived' | 'status'>>): HomeFilter {
  const filters = experiments.map(homeFilterOf);
  if (filters.includes('live')) return 'live';
  return filters.includes('drafts') ? 'drafts' : 'live';
}

/** Home: the seller's tests. "New test" starts at the path chooser. */
export default function Home() {
  const companyId = useCompanyId();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<HomeFilter>('live');
  const [overview, setOverview] = useState<Record<string, TestOverview>>({});
  const [overviewFailed, setOverviewFailed] = useState(false);
  const { plan, refresh: refreshPlan } = usePlan(companyId);
  // 'new' came from pressing New test, which carries on to the chooser after.
  const [upgrade, setUpgrade] = useState<'new' | 'line' | null>(null);
  const line = planLine(plan);

  function startNewTest() {
    if (needsProForNewTest(plan)) setUpgrade('new');
    else navigate('/new');
  }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await api.listExperiments(companyId, undefined, true, true);
      setExperiments(list);
      setFilter(initialHomeFilter(list));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  // Card details load after the list so it can render right away.
  const loadOverview = useCallback(async () => {
    setOverviewFailed(false);
    try {
      const items = await api.getOverview(companyId);
      setOverview(Object.fromEntries(items.map((i) => [i.experimentId, i])));
    } catch (err) {
      console.error('Failed to load test details:', err);
      setOverviewFailed(true);
    }
  }, [companyId]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const shown = useMemo(() => experiments.filter((e) => homeFilterOf(e) === filter), [experiments, filter]);
  const counts = useMemo(() => {
    const c: Record<HomeFilter, number> = { live: 0, drafts: 0, ended: 0 };
    for (const e of experiments) c[homeFilterOf(e)] += 1;
    return c;
  }, [experiments]);

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)' }}>
        <Heading size={isMobile ? '6' : '7'}>Your price tests</Heading>
        <Button size="3" variant="solid" onClick={startNewTest}>
          <Plus16 />
          New test
        </Button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
          <Spinner size="3" />
        </div>
      ) : error ? (
        <Card size="3" style={{ textAlign: 'center' }}>
          <Text color="red" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            Couldn't load your tests: {error}
          </Text>
          <Button
            onClick={() => {
              load();
              loadOverview();
            }}
            variant="solid"
          >
            Try again
          </Button>
        </Card>
      ) : experiments.length === 0 ? (
        <Rise>
          <Card size="3">
            <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
              Test your price where your buyers already are
            </Heading>
            <Text size="3" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
              Tell us where your buyers find you — your bio, your free whop, your partners — and
              we'll set up a test right there. Half see today's price, half see a new one, and you
              see which earns more per visitor.
            </Text>
            <Button size="3" variant="solid" onClick={startNewTest}>
              Start your first test
            </Button>
          </Card>
        </Rise>
      ) : (
        <>
          <div role="group" aria-label="Show" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {FILTERS.map((f) => (
              <Button
                key={f.id}
                size="2"
                variant={filter === f.id ? 'solid' : 'surface'}
                color={filter === f.id ? undefined : 'gray'}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
                {counts[f.id] > 0 && <span className="vara2-tabular">{counts[f.id]}</span>}
              </Button>
            ))}
          </div>

          {overviewFailed && <LoadFailed what="the numbers on these tests" onRetry={loadOverview} />}

          {shown.length === 0 ? (
            <Card size="2">
              <Text color="gray">
                {filter === 'live'
                  ? counts.drafts > 0
                    ? 'No live tests. Go live with one from Drafts & paused, or start a new one.'
                    : 'No live tests. Start one to see results here.'
                  : filter === 'drafts'
                    ? "No drafts or paused tests. Tests you haven't started, or have paused, show up here."
                    : 'No ended tests yet. When you pick a winner, the test moves here.'}
              </Text>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {shown.map((exp, idx) => (
                <Rise key={exp._id} delay={Math.min(idx, 8) * 50}>
                  <TestCard experiment={exp} overview={overview[exp._id]} />
                </Rise>
              ))}
            </div>
          )}
        </>
      )}

      {line && (
        <Rise>
          <Text as="p" size="2" color="gray" style={{ textAlign: 'center' }}>
            {line.text}
            {line.action && ' '}
            {line.action?.kind === 'upgrade' && (
              <Button variant="ghost" size="1" onClick={() => setUpgrade('line')}>
                {line.action.label}
              </Button>
            )}
            {line.action?.kind === 'update_card' && (
              <a href={WHOP_MEMBERSHIPS_URL} target="_blank" rel="noopener noreferrer">
                {line.action.label}
              </a>
            )}
          </Text>
        </Rise>
      )}

      {upgrade && (
        <UpgradeDialog
          companyId={companyId}
          onClose={() => setUpgrade(null)}
          onUpgraded={() => {
            if (upgrade === 'new') {
              navigate('/new');
              return;
            }
            setUpgrade(null);
            refreshPlan();
          }}
        />
      )}
    </div>
  );
}
